import { createContext, type PropsWithChildren, useContext, useState } from 'react';

import { createStore, useStore } from 'zustand';

import type { InputMethod, PlatformType, SessionType } from './session.types';

type Props = {
    sessionType: SessionType;
    platform: PlatformType;
    inputMethod: InputMethod;
};

type State = Props & {
    setSessionType: (sessionType: SessionType) => void;
    setPlatform: (platform: PlatformType) => void;
};

type SessionRecordingStore = ReturnType<typeof createSessionRecordingStore>;

const createSessionRecordingStore = (initProps: Props) => {
    return createStore<State>()((set) => ({
        ...initProps,
        setSessionType: (sessionType: SessionType) => set({ sessionType }),
        setPlatform: (platform: PlatformType) => set({ platform }),
    }));
};

const SessionRecordingContext = createContext<SessionRecordingStore | null>(null);

type ProviderProps = PropsWithChildren<Props>;

export function SessionRecordingProvider({ children, ...props }: ProviderProps) {
    const [store] = useState(() => createSessionRecordingStore(props));
    return (
        <SessionRecordingContext.Provider value={store}>
            {children}
        </SessionRecordingContext.Provider>
    );
}

export function useSessionRecordingStore<T>(selector: (state: State) => T): T {
    const store = useContext(SessionRecordingContext);
    if (!store) throw new Error('Missing SessionRecordingProvider');
    return useStore(store, selector);
}
